// Import React hooks
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// Import auth context
import { AuthContext } from "global/context/AuthContext";

// Import API calls
import {
    addAIGenerationRequest,
    getAIGenerationQueueItem,
} from "api/APIStrichCalls";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Do not allow another upscale once the current result exceeds this dimension
const MAX_UPSCALE_DIMENSION = 20000;

// Export the custom hook
export default function usePodDesignUpscaleFlow({
    sourceImage,
    sourceFileType,
    sourceWidth,
    sourceHeight,
    setSubmittedUpscaledImage,
    setShowPodDesignUpscalePage,
}) {

    // Get auth values
    const { token, authReady } = useContext(AuthContext);

    // Get toast helpers
    const { toastError } = useToast();

    // Keep the latest toast helper available without restarting queue polling
    const toastErrorRef = useRef(toastError);
    toastErrorRef.current = toastError;

    // Use the same default upscaler configured by the upscale workflow
    const [selectedUpscaler, setSelectedUpscaler] = useState("siax");

    // These are the three models exposed by the Upscale workflow
    const upscaleOptions = [
        {
            value: "ultrasharp",
            label: "UltraSharp",
            description: "Uses the 4x UltraSharp model. Try this when you want a crisp upscale with strong edge definition.",
        },
        {
            value: "siax",
            label: "Siax",
            description: "Uses the 4x Siax model. This is the default upscaler used by the workflow.",
        },
        {
            value: "esrgan",
            label: "ESRGAN",
            description: "Uses the ESRGAN 4x model. This gives you an alternative result to compare against the other upscale models.",
        },
    ];

    // This stores the current completed upscale result
    const [upscaledImage, setUpscaledImage] = useState(null);

    // This stores the active queue item separately for the progress display
    const [upscaleQueueItem, setUpscaleQueueItem] = useState(null);

    // This tracks whether upscaling is in progress
    const [isUpscaling, setIsUpscaling] = useState(false);

    // This stores the queue job id
    const [upscaledImageJobID, setUpscaledImageJobID] = useState(null);

    // Get the description for the currently selected upscaler
    const selectedUpscalerInfo = upscaleOptions.find((option) => option.value === selectedUpscaler) || upscaleOptions[0];

    // Normalise the stored file type supplied by the design role
    const normalizedSourceFileType = String(sourceFileType || "").toLowerCase();

    // Check whether the working image has been upscaled at least once
    const hasUpscaledImage = Boolean(upscaledImage?.image_url);

    // Read the definitive type of the completed upscale result
    const upscaledFileType = String(upscaledImage?.bucket?.file_type || "").toLowerCase();

    // The right-hand preview starts with the original and is replaced after each upscale
    const currentImageUrl = hasUpscaledImage
        ? upscaledImage.image_url
        : sourceImage;

    // Keep the working image format in step with the image currently on the right
    const currentFileType = hasUpscaledImage
        ? upscaledFileType
        : normalizedSourceFileType;

    // Read the dimensions of the image currently on the right
    const currentWidth = hasUpscaledImage
        ? Number(upscaledImage.bucket.metadata.width_px)
        : Number(sourceWidth);

    const currentHeight = hasUpscaledImage
        ? Number(upscaledImage.bucket.metadata.height_px)
        : Number(sourceHeight);

    // Check that the current image has valid stored dimensions
    const hasCurrentDimensions =
        Number.isFinite(currentWidth) &&
        Number.isFinite(currentHeight) &&
        currentWidth > 0 &&
        currentHeight > 0;

    // Stop another upscale once the current image exceeds the size limit
    const upscaleLimitReached =
        hasCurrentDimensions &&
        (
            currentWidth > MAX_UPSCALE_DIMENSION ||
            currentHeight > MAX_UPSCALE_DIMENSION
        );

    // Reset is only useful after an upscale has completed
    const canReset =
        hasUpscaledImage &&
        isUpscaling === false;

    // Upscale always operates on the image currently shown on the right
    const canUpscale =
        !!currentImageUrl &&
        !!selectedUpscaler &&
        (currentFileType === "jpg" || currentFileType === "png") &&
        hasCurrentDimensions &&
        upscaleLimitReached === false &&
        isUpscaling === false;

    // Submit is only available once an upscale result exists
    const canSubmit =
        hasUpscaledImage &&
        (upscaledFileType === "jpg" || upscaledFileType === "png") &&
        isUpscaling === false;



    // Fetch queue item and update the local queue item state
    const fetchUpscaledImage = useCallback(async (jobID) => {

        try {

            // Fetch the queue item we are tracking
            const queueResponse = await getAIGenerationQueueItem(token, jobID);

            // Stop if the response is not successful
            if (!queueResponse?.success) {

                // Show the queue retrieval error
                toastErrorRef.current({
                    title: "Error",
                    message: "We could not retrieve the upscaled image. Please try again.",
                    autoCloseMs: 6000,
                });

                // Clear the failed job
                setUpscaledImageJobID(null);

                // Stop loading
                setIsUpscaling(false);

                return;

            }

            // Get the returned queue item safely
            const queueImage = queueResponse.results?.[0] || null;

            // Keep the latest queue item data for the progress display
            setUpscaleQueueItem(queueImage);

            // Check whether the queue item failed
            const hasQueueError =
                Boolean(queueImage?.error_message) ||
                String(queueImage?.status || "").toLowerCase() === "errored";

            if (hasQueueError) {

                // Start without error text so the API details can be used when available
                let queueErrorMessage = "";

                // Use a plain string error message directly
                if (typeof queueImage?.error_message === "string" && queueImage.error_message.trim()) {
                    queueErrorMessage = queueImage.error_message.trim();
                }

                // Use the detailed API error when an error object was returned
                if (!queueErrorMessage && queueImage?.error_message?.details) {
                    queueErrorMessage = queueImage.error_message.details;
                }

                // Append the workflow mapping error when one was supplied
                if (queueImage?.error_message?.map_error) {
                    queueErrorMessage += (queueErrorMessage ? " " : "") + queueImage.error_message.map_error;
                }

                // Fall back to a useful message when no detail was supplied
                if (!queueErrorMessage) {
                    queueErrorMessage = "We could not upscale this image. Please try again.";
                }

                // Show the error to the user
                toastErrorRef.current({ title: "Error", message: queueErrorMessage, autoCloseMs: 6000 });

                // Clear the failed job
                setUpscaledImageJobID(null);

                // Stop loading
                setIsUpscaling(false);

                return;

            }

            // When the queue item has produced an image, make it the new working image
            if (queueImage?.image_url) {

                // Replace the current completed result
                setUpscaledImage(queueImage);

                // Clear the completed job
                setUpscaledImageJobID(null);

                // Stop loading
                setIsUpscaling(false);

            }

        } catch (error) {

            // Show the queue retrieval error
            toastErrorRef.current({
                title: "Error",
                message: "We could not retrieve the upscaled image. Please try again.",
                autoCloseMs: 6000,
            });

            // Clear the failed job
            setUpscaledImageJobID(null);

            // Stop loading
            setIsUpscaling(false);

        }

    }, [token]);



    // Poll the queue item while upscaling is in progress
    useEffect(() => {

        // Stop if auth is not ready
        if (!authReady) { return; }

        // Stop if there is nothing to poll
        if (!isUpscaling || !upscaledImageJobID) { return; }

        // Run an immediate fetch
        fetchUpscaledImage(upscaledImageJobID);

        // Start the polling interval
        const pollInterval = setInterval(() => {
            fetchUpscaledImage(upscaledImageJobID);
        }, 2000);

        // Clear the polling interval when the component unmounts or the job changes
        return () => clearInterval(pollInterval);

    }, [authReady, isUpscaling, upscaledImageJobID, fetchUpscaledImage]);



    // Run the Upscale workflow against the image currently shown in the preview
    const handleUpscaleImage = async () => {

        // Stop when the current image cannot be upscaled
        if (!canUpscale) { return; }

        // Clear the previous queue item without clearing the working image
        setUpscaleQueueItem(null);

        // Clear the previous completed job before starting another one
        setUpscaledImageJobID(null);

        // Start loading
        setIsUpscaling(true);

        // Build the exact settings required by the Upscale workflow
        const normalizedSettings = {
            input_image: currentImageUrl,
            upscale_model: selectedUpscaler,
            output_format: currentFileType,
        };

        // Build the queue payload using the known Upscale workflow code
        const payload = {
            settings: normalizedSettings,
            workflow_code: "upscale",
        };

        // Submit the job
        const response = await addAIGenerationRequest(token, payload);

        // Handle success
        if (response?.success) {

            // Store the new job id
            setUpscaledImageJobID(response.results?.[0]?.job || null);

            // Keep the loading state active while the queue item runs
            setIsUpscaling(true);

            return;

        }

        // Build the API error message
        const errorMessage = "Could not run the upscaler: " + (response?.error_message || "Unknown error");

        // Show the error
        toastError({ title: "Error", message: errorMessage, autoCloseMs: 6000 });

        // Stop loading
        setIsUpscaling(false);

    };



    // Reset the working image back to the original design image
    const handleResetUpscale = () => {

        // Stop while a job is running
        if (isUpscaling) { return; }

        // Clear the completed upscale result
        setUpscaledImage(null);

        // Clear the previous queue item
        setUpscaleQueueItem(null);

        // Clear the previous job id
        setUpscaledImageJobID(null);

    };





    // This submits the final upscaled image back to the design page
    const handleSubmitUpscaledImage = async () => {

        // Stop if the upscaled image or its stored result type is missing
        if (
            !upscaledImage?.image_url ||
            (upscaledFileType !== "jpg" && upscaledFileType !== "png")
        ) {
            return;
        }

        // Build the role image payload
        const payload = {
            imageDetails: {
                image_url: upscaledImage.image_url,
                thumbnail_url: upscaledImage.thumbnail_url,

                // Keep the definitive type returned by the AI bucket
                file_type: upscaledFileType,

                // Keep the stored result metadata including its new dimensions
                metadata: upscaledImage.bucket.metadata,

                source: "ai-upscaled",
                name: "Upscaled image",
            },
            uploadedImage: upscaledImage.image_url,
        };

        // Return the payload to the parent page
        setSubmittedUpscaledImage((previousValue) => {

            // Keep the existing values safely
            const safePreviousValue = previousValue ?? {};

            // Return the updated object
            return {
                ...safePreviousValue,
                payload,
            };

        });

        // Close the popup
        setShowPodDesignUpscalePage(false);

    };



    // Return everything the page needs
    return {
        selectedUpscaler,
        setSelectedUpscaler,
        upscaleOptions,
        selectedUpscalerInfo,
        upscaleQueueItem,
        isUpscaling,
        currentImageUrl,
        currentWidth,
        currentHeight,
        upscaleLimitReached,
        canReset,
        canUpscale,
        canSubmit,
        handleResetUpscale,
        handleUpscaleImage,
        handleSubmitUpscaledImage,
    };

}